import { LitElement, html } from 'lit'
import { ModalSaveMixin } from '../core/mixins/modal-save.mixin.js'
import { orcamentoCacheService } from '../core/services/orcamento-cache.service.js'
import { categoriaCacheService } from '../core/services/categoria-cache.service.js'
import './modal-categoria.js'

/**
 * Modal para criação e edição de orçamentos por categoria
 * Permite definir valores orçados para categorias específicas
 *
 * Modo criação (show() sem argumentos):
 * - Exibe select com categorias disponíveis (sem orçamento ativo no período)
 * - Quando todas categorias já possuem orçamento: mensagem informativa,
 *   select e botão salvar desabilitados
 *
 * Modo edição (show(budget) com orçamento):
 * - Exibe categoria vinculada (select desabilitado)
 * - Preenche valor orçado atual
 *
 * Utiliza ModalSaveMixin para gerenciar o ciclo de salvamento:
 * - Loading no botão Salvar durante operação
 * - Chamada direta ao orcamentoCacheService (criar/atualizar)
 * - Erro inline com data-cy="erro-salvar-orcamento"
 * - Guard contra clique duplo e fechamento durante saving
 *
 * Layout: flexbox com header fixo, conteúdo com scroll independente e footer fixo
 * com botões de ação sempre visíveis (padrão 040-modal-scroll-layout-padronizado)
 */
class ModalOrcamento extends ModalSaveMixin(LitElement) {
  static properties = {
    budget: { type: Object },
    categoriasDisponiveis: { type: Array },
    _categoriaSelecionadaId: { type: String, state: true },
    _todasCategoriasPossuemOrcamento: { type: Boolean, state: true }
  }

  /**
   * Nome da entidade para eventos e data-cy de erro.
   * Usado pelo ModalSaveMixin para gerar:
   * - data-cy="erro-salvar-orcamento"
   * - detail.entidade nos eventos cache-operation-started/finished
   * @type {string}
   */
  _entidade = 'orcamento'

  createRenderRoot () {
    return this
  }

  constructor () {
    super()
    this.open = false
    this.budget = null
    /** @type {Array} Categorias disponíveis para criação (sem orçamento ativo no período) */
    this.categoriasDisponiveis = []
    /** @type {string} ID da categoria selecionada no select (modo criação) */
    this._categoriaSelecionadaId = ''
    /** @type {boolean} Indica se todas as categorias já possuem orçamento ativo no período */
    this._todasCategoriasPossuemOrcamento = false
    /** @type {Set<string>} Snapshot dos IDs de categorias antes de abrir modal-categoria */
    this._snapshotCategoriasIds = null
    /** @type {Function} Referência ao handler do evento categorias-cache-updated para remoção */
    this._handleCategoriasCacheUpdated = this._onCategoriasCacheUpdated.bind(this)
  }

  /**
   * Indica se o modal está em modo de edição.
   * Determinado pela presença de um budget com id ao abrir o modal.
   * @returns {boolean} True se editando orçamento existente
   */
  get isEditMode () {
    return !!(this.budget && this.budget.id)
  }

  /**
   * Exibe o modal para criação ou edição de orçamento.
   * Em modo criação (sem argumento): carrega categorias disponíveis
   * (sem orçamento ativo no período corrente) e registra listener
   * para o evento `categorias-cache-updated` para detectar novas categorias.
   * Em modo edição (com budget): preenche o campo de valor com o valor existente.
   * @param {Object|null} budget - Dados do orçamento para edição, ou null para criação
   */
  show (budget = null) {
    this.budget = budget
    this.open = true
    this._categoriaSelecionadaId = ''
    this._todasCategoriasPossuemOrcamento = false
    this.categoriasDisponiveis = []
    this._snapshotCategoriasIds = null
    this.requestUpdate()

    if (budget) {
      // Modo edição: preenche o valor orçado atual formatado em BRL
      setTimeout(() => {
        const input = this.renderRoot.querySelector('[data-cy="input-valor-orcado"]')
        if (input) {
          const valor = budget.valorLimite || budget.valorOrcado || budget.budget || 0
          input.value = Math.abs(valor).toLocaleString('pt-BR', {
            style: 'currency',
            currency: 'BRL'
          })
        }
      }, 100)
    } else {
      // Modo criação: carrega categorias disponíveis no período corrente
      this._carregarCategoriasDisponiveis()
      // Registra listener para detectar novas categorias criadas via modal-categoria
      this._adicionarListenerCategorias()
      // Suprime criação automática de orçamento para novas categorias
      // (o usuário vai definir o valor manualmente ao salvar)
      orcamentoCacheService.suprimirCriacaoAutomatica = true
    }
  }

  /**
   * Sobrescreve o método close para remover o listener de categorias-cache-updated.
   * Garante que o listener só fica ativo enquanto o modal está aberto,
   * evitando memory leaks e reações indesejadas quando o modal está fechado.
   */
  close () {
    this._removerListenerCategorias()
    this._snapshotCategoriasIds = null
    // Restaura criação automática ao fechar o modal
    orcamentoCacheService.suprimirCriacaoAutomatica = false
    super.close()
  }

  /**
   * Registra o listener global para o evento `categorias-cache-updated`.
   * O listener fica ativo apenas enquanto o modal está aberto em modo criação,
   * permitindo detectar quando uma nova categoria é criada via modal-categoria.
   * @private
   */
  _adicionarListenerCategorias () {
    window.addEventListener('categorias-cache-updated', this._handleCategoriasCacheUpdated)
  }

  /**
   * Remove o listener global do evento `categorias-cache-updated`.
   * Chamado ao fechar o modal para evitar memory leaks e reações fora de contexto.
   * @private
   */
  _removerListenerCategorias () {
    window.removeEventListener('categorias-cache-updated', this._handleCategoriasCacheUpdated)
  }

  /**
   * Handler do evento `categorias-cache-updated`.
   * Compara o snapshot de IDs de categorias salvo antes de abrir o modal-categoria
   * com a lista atual para identificar a nova categoria criada.
   * Se detectar uma nova categoria:
   * - Recarrega a lista de categorias disponíveis no select
   * - Seleciona automaticamente a nova categoria criada
   * Se nenhuma nova categoria for detectada (usuário fechou sem criar):
   * - O formulário permanece inalterado
   * @private
   */
  async _onCategoriasCacheUpdated () {
    // Se não há snapshot, significa que o evento não foi disparado após abrir modal-categoria
    // Neste caso, apenas recarrega as categorias disponíveis
    const snapshotAnterior = this._snapshotCategoriasIds

    // Recarrega categorias disponíveis para atualizar o select
    await this._carregarCategoriasDisponiveis()

    // Se temos um snapshot anterior, detecta a nova categoria
    if (snapshotAnterior) {
      const categoriasAtuaisIds = new Set(this.categoriasDisponiveis.map(cat => cat.id))

      // Encontra IDs que existem agora mas não existiam antes (nova categoria)
      let novaCategoriaId = null
      for (const id of categoriasAtuaisIds) {
        if (!snapshotAnterior.has(id)) {
          novaCategoriaId = id
          break
        }
      }

      // Se detectou nova categoria, seleciona automaticamente no select
      if (novaCategoriaId) {
        this._categoriaSelecionadaId = novaCategoriaId
        this.requestUpdate()
      }
    }

    // Limpa o snapshot após processamento
    this._snapshotCategoriasIds = null
  }

  /**
   * Carrega categorias que não possuem orçamento ativo no período corrente.
   * Consulta categoriaCacheService para obter todas as categorias e
   * orcamentoCacheService para obter orçamentos ativos do mês atual.
   * Filtra removendo categorias que já possuem orçamento com ativo !== false.
   * Atualiza _todasCategoriasPossuemOrcamento quando não há categorias disponíveis.
   * @returns {Promise<void>}
   * @private
   */
  async _carregarCategoriasDisponiveis () {
    try {
      const todasCategorias = await categoriaCacheService.listar()
      const agora = new Date()
      const mesAtual = String(agora.getMonth() + 1).padStart(2, '0')
      const anoAtual = String(agora.getFullYear())
      const orcamentosAtivos = await orcamentoCacheService.listarPorPeriodo(mesAtual, anoAtual)

      // Mapeia IDs de categorias que já possuem orçamento ativo COM valor definido.
      // Orçamentos automáticos (valorLimite=0 e valorOrcado=0) não bloqueiam a categoria
      // no select, pois serão substituídos pela criação manual do usuário.
      const categoriasComOrcamento = new Set(
        orcamentosAtivos
          .filter(orc => orc.ativo !== false && ((orc.valorLimite || 0) > 0 || (orc.valorOrcado || 0) > 0))
          .map(orc => orc.categoriaId)
      )

      // Filtra apenas categorias sem orçamento ativo com valor definido
      this.categoriasDisponiveis = todasCategorias.filter(
        cat => !categoriasComOrcamento.has(cat.id)
      )

      // Identifica se todas as categorias já possuem orçamento
      this._todasCategoriasPossuemOrcamento = todasCategorias.length > 0 && this.categoriasDisponiveis.length === 0
    } catch (error) {
      console.error('Erro ao carregar categorias disponíveis:', error)
      this.categoriasDisponiveis = []
      this._todasCategoriasPossuemOrcamento = false
    }
  }

  /**
   * Fecha o modal ao clicar no backdrop (área escurecida)
   * Verifica se o clique foi diretamente no backdrop (target === currentTarget)
   * para evitar que cliques em elementos filhos (como o modal-categoria)
   * propaguem e fechem o modal-orcamento indevidamente.
   * Delega para this.close() que já gerencia o estado do modal
   * (ModalSaveMixin impede fechamento durante _saving)
   * @param {MouseEvent} e - Evento de clique
   */
  _handleBackdropClick (e) {
    // Só fecha se o clique foi diretamente no backdrop (não em um filho como modal-categoria)
    if (e.target === e.currentTarget) {
      this.close()
    }
  }

  _stopPropagation (e) {
    e.stopPropagation()
  }

  /**
   * Handler para mudança no select de categoria.
   * Armazena o ID da categoria selecionada no estado interno.
   * @param {Event} e - Evento de change do select
   * @private
   */
  _handleCategoriaChange (e) {
    this._categoriaSelecionadaId = e.target.value
  }

  /**
   * Aplica efeito de "acender" ao passar o mouse sobre o botão Salvar
   * Replica o padrão do floating-button com transição suave de cor
   * @param {Event} e - Evento de mouseenter
   * @private
   */
  _onSaveHover (e) {
    e.target.style.backgroundColor = 'var(--hover-primary, #7B3EF0)'
  }

  /**
   * Restaura cor original do botão Salvar ao remover o mouse
   * @param {Event} e - Evento de mouseleave
   * @private
   */
  _onSaveLeave (e) {
    e.target.style.backgroundColor = ''
  }

  /**
   * Aplica efeito de "acender" ao passar o mouse sobre o botão Cancelar
   * Usa background secundário para destacar o botão neutro
   * @param {Event} e - Evento de mouseenter
   * @private
   */
  _onCancelHover (e) {
    e.target.style.backgroundColor = 'var(--glass-bg, rgba(117, 133, 255, 0.22))'
  }

  /**
   * Restaura cor original do botão Cancelar ao remover o mouse
   * @param {Event} e - Evento de mouseleave
   * @private
   */
  _onCancelLeave (e) {
    e.target.style.backgroundColor = ''
  }

  /**
   * Retorna o período corrente no formato YYYY-MM
   * Usado para informar ao backend qual mês/ano do orçamento a ser criado.
   * @returns {string} Período no formato "YYYY-MM" (ex: "2026-06")
   * @private
   */
  _getPeriodoCorrente () {
    const agora = new Date()
    const mes = String(agora.getMonth() + 1).padStart(2, '0')
    const ano = String(agora.getFullYear())
    return `${ano}-${mes}`
  }

  /**
   * Formata o valor digitado no campo de orçamento como moeda BRL.
   * Remove caracteres não numéricos, divide por 100 e formata com
   * Intl (R$ X.XXX,XX). Mesmo padrão do modal de lançamentos.
   * @param {Event} e - Evento de input no campo de valor
   * @private
   */
  _formatCurrencyInput (e) {
    let value = e.target.value.replace(/\D/g, '')
    if (value === '') {
      e.target.value = ''
      return
    }

    const numericValue = parseInt(value) / 100
    e.target.value = numericValue.toLocaleString('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    })
  }

  /**
   * Extrai o valor numérico do campo de valor orçado formatado em BRL.
   * Remove caracteres não numéricos exceto vírgula, troca vírgula por ponto
   * e converte para float. Retorna 0 se campo vazio.
   * @returns {number} Valor numérico extraído do campo formatado
   * @private
   */
  _getValorOrcado () {
    const input = this.renderRoot.querySelector('[data-cy="input-valor-orcado"]')
    if (!input) return 0
    const value = input.value || ''
    const numericValue = value.replace(/[^\d,]/g, '').replace(',', '.')
    return parseFloat(numericValue || 0)
  }

  /**
   * Abre o modal-categoria em modo de criação sobreposto ao modal de orçamento.
   * Preserva o estado atual do formulário (valor informado) enquanto o modal
   * de categoria está aberto. Permite criar nova categoria sem perder dados.
   * Salva um snapshot dos IDs das categorias disponíveis antes de abrir o modal,
   * para posterior comparação ao receber o evento `categorias-cache-updated`
   * e detectar qual categoria foi criada.
   * @private
   */
  _abrirModalCategoria () {
    // Salva snapshot dos IDs de categorias antes de abrir modal-categoria
    // para comparação posterior ao receber evento categorias-cache-updated
    this._snapshotCategoriasIds = new Set(this.categoriasDisponiveis.map(cat => cat.id))

    const modal = this.renderRoot.querySelector('modal-categoria')
    if (modal) {
      modal.show(null)
    }
  }

  /**
   * Executa a operação de salvamento via orcamentoCacheService.
   *
   * Validações aplicadas antes do salvamento:
   * - Modo edição: valor deve ser > 0 (impede salvamento com valor <= 0)
   * - Modo criação: valor não pode ser negativo (< 0)
   * - Modo criação: categoria deve estar selecionada (_categoriaSelecionadaId não vazio)
   *
   * Utiliza _executeSave do ModalSaveMixin que gerencia:
   * - Guard contra clique duplo (_saving = true → retorna)
   * - Disparo de cache-operation-started/finished
   * - Timeout de 30s
   * - Tratamento de erro inline (modal permanece aberto com dados preservados)
   * - Fechamento automático após sucesso
   *
   * Em caso de falha ao salvar:
   * - O modal permanece aberto com todos os dados preenchidos
   * - Mensagem de erro inline é exibida via renderErroSalvar() (data-cy="erro-salvar-orcamento")
   *
   * Em modo criação, inclui o categoriaId selecionado nos dados.
   * Em modo edição, utiliza os campos legados (category/budget) para retrocompatibilidade.
   * @returns {Promise<void>}
   */
  async _handleSave () {
    const value = this._getValorOrcado()

    // Validação: em edição, valor deve ser maior que zero (impede salvamento com valor <= 0)
    if (this.isEditMode && value <= 0) {
      return
    }

    // Validação: em criação, valor não pode ser negativo (impede salvamento com valor < 0)
    if (!this.isEditMode && value < 0) {
      return
    }

    // Validação: em criação, categoria deve estar selecionada (redundante com disabled do botão)
    if (!this.isEditMode && !this._categoriaSelecionadaId) {
      return
    }

    const operacao = this.isEditMode ? 'atualizar' : 'criar'
    const dados = this.isEditMode
      ? { valorLimite: value, valorOrcado: value }
      : (() => {
          const periodo = this._getPeriodoCorrente()
          const [ano, mes] = periodo.split('-')
          return {
            categoriaId: this._categoriaSelecionadaId,
            valorOrcado: value,
            valorLimite: value,
            periodo,
            mesAno: `${mes}/${ano}`
          }
        })()
    const id = this.isEditMode ? this.budget.id : undefined

    await this._executeSave(orcamentoCacheService, operacao, dados, id)
  }

  render () {
    return html`
      <div 
        class="modal ${this.open ? 'is-active' : ''}" 
        @click="${this._handleBackdropClick}"
        style="
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background-color: rgba(0, 0, 0, 0.5);
          z-index: 20000;
          display: ${this.open ? 'flex' : 'none'};
          align-items: center;
          justify-content: center;
        "
      >
        <div 
          class="card"
          @click="${this._stopPropagation}"
          style="
            width: 90%;
            max-width: 400px;
            max-height: 85vh;
            display: flex;
            flex-direction: column;
            overflow: hidden;
            border-radius: var(--radius-card, 20px);
            box-shadow: var(--shadow-card, 0 10px 40px rgba(0,0,0,0.35));
            position: relative;
          "
        >
          <!-- Zona 1: Header fixo no topo com sombra sobre o conteúdo -->
          <header class="card-header" style="flex-shrink: 0; position: relative; z-index: 2; box-shadow: var(--shadow-header-modal, 0 4px 12px rgba(0,0,0,0.3));">
            ${this.renderBotaoVoltarIOS()}
            <p class="card-header-title" data-cy="modal-orcamento-title">
              ${this.isEditMode ? `Orçamento - ${this.budget?.categoria?.nome || this.budget?.category || ''}` : 'Novo Orçamento'}
            </p>
            ${this.renderBotaoFechar('modal-orcamento-close')}
          </header>

          <!-- Zona 2: Conteúdo com scroll independente -->
          <div class="card-content custom-scrollbar" style="
            flex: 1;
            overflow-y: auto;
            overflow-x: hidden;
            min-height: 0;
          " @input="${this._clearError}" @change="${this._clearError}">

            <!-- Mensagem quando todas as categorias já possuem orçamento no período -->
            ${!this.isEditMode && this._todasCategoriasPossuemOrcamento ? html`
              <div class="notification is-warning is-light mb-4" data-cy="msg-todas-categorias-com-orcamento" style="
                background-color: var(--bg-notification-warning, rgba(255, 221, 87, 0.1));
                color: var(--text-notification-warning, #FFB84D);
                border-radius: var(--radius-input, 12px);
                padding: 0.75rem 1rem;
              ">
                <p class="is-size-7">Todas as categorias já possuem orçamento no período corrente</p>
              </div>
            ` : ''}

            <!-- Campo de seleção de categoria -->
            <div class="field mb-4">
              <label class="label is-size-7 has-text-weight-medium">Categoria</label>
              ${this.isEditMode ? html`
                <!-- Modo edição: select desabilitado com categoria vinculada -->
                <div class="control">
                  <div class="select is-fullwidth">
                    <select
                      data-cy="select-categoria-orcamento"
                      disabled
                    >
                      <option value="${this.budget?.categoriaId || this.budget?.categoria?.id || ''}" selected>
                        ${this.budget?.categoria?.emoji || ''} ${this.budget?.categoria?.nome || this.budget?.category || ''}
                      </option>
                    </select>
                  </div>
                </div>
              ` : html`
                <!-- Modo criação: select com categorias disponíveis + botão criar categoria -->
                <div class="field has-addons" style="margin-bottom: 0;">
                  <div class="control is-expanded">
                    <div class="select is-fullwidth">
                      <select
                        data-cy="select-categoria-orcamento"
                        ?disabled="${this._todasCategoriasPossuemOrcamento}"
                        @change="${this._handleCategoriaChange}"
                        .value="${this._categoriaSelecionadaId || ''}"
                      >
                        <option value="" disabled ?selected="${!this._categoriaSelecionadaId}">Selecione a categoria</option>
                        ${this.categoriasDisponiveis.map(cat => html`
                          <option value="${cat.id}" ?selected="${cat.id === this._categoriaSelecionadaId}">${cat.emoji || ''} ${cat.nome}</option>
                        `)}
                      </select>
                    </div>
                  </div>
                  <div class="control">
                    <button
                      type="button"
                      class="button"
                      data-cy="btn-criar-categoria-orcamento"
                      @click="${this._abrirModalCategoria}"
                      title="Criar nova categoria"
                      style="
                        border-color: var(--border, #26304A);
                        color: var(--text-action, #7585FF);
                      "
                    >
                      <span class="icon is-small">
                        <i class="fas fa-plus"></i>
                      </span>
                    </button>
                  </div>
                </div>
              `}
            </div>

            <!-- Campo de valor orçado -->
            <div class="field mb-4">
              <label class="label is-size-7 has-text-weight-medium">Valor Orçado</label>
              <div class="control">
                <input
                  class="input"
                  type="text"
                  placeholder="R$ 0,00"
                  data-cy="input-valor-orcado"
                  @input="${this._formatCurrencyInput}"
                />
              </div>
            </div>

            <!-- Mensagem de erro inline posicionada abaixo dos campos e acima do footer -->
            ${this.renderErroSalvar()}
          </div>

          <!-- Zona 3: Footer fixo com botões de ação e sombra sobre o conteúdo -->
          <footer class="card-footer"
            data-cy="footer-modal-orcamento"
            aria-label="Ações do modal de orçamento"
            style="
              flex-shrink: 0;
              padding: 1rem 1.5rem;
              background-color: var(--bg-surface, #151C30);
              border-top: 1px solid var(--border, #26304A);
              position: relative;
              z-index: 2;
              box-shadow: var(--shadow-footer-modal, 0 -4px 12px rgba(0,0,0,0.3));
            "
          >
            <div class="field is-grouped is-justify-content-flex-end" style="width: 100%">
              <div class="control">
                <button 
                  class="button" 
                  type="button" 
                  data-cy="btn-cancelar-orcamento"
                  ?disabled="${this._saving}"
                  @click="${this.close}"
                  @mouseenter="${this._onCancelHover}"
                  @mouseleave="${this._onCancelLeave}"
                >
                  Cancelar
                </button>
              </div>
              <div class="control">
                <button 
                  class="button is-primary ${this._saving ? 'is-loading' : ''}" 
                  type="button" 
                  data-cy="btn-salvar-orcamento"
                  ?disabled="${this._saving || this._todasCategoriasPossuemOrcamento || (!this.isEditMode && !this._categoriaSelecionadaId)}"
                  @click="${this._handleSave}"
                  @mouseenter="${this._onSaveHover}"
                  @mouseleave="${this._onSaveLeave}"
                >
                  Salvar
                </button>
              </div>
            </div>
          </footer>
        </div>

        <!-- modal-categoria sobreposto para criação de nova categoria -->
        <modal-categoria></modal-categoria>
      </div>
    `
  }
}

customElements.define('modal-orcamento', ModalOrcamento)
